/**
 * Auto-reminder details modal - the full status view opened from the enabled
 * status button: cadence, last sent, next send, template, recipient; plus
 * per-record cadence editing, an auto-cancel toggle, and a "Cancel auto-reminder"
 * action guarded by a nested confirmation. Resource-agnostic.
 */
import { useEffect, useState } from 'react'
import { BellRing, CalendarClock, Mail, Send } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Separator } from '@/shared/components/ui/separator'
import { Switch } from '@/shared/components/ui/switch'
import { toastApiError } from '@/shared/lib/api-error'

import {
  type AutoReminderStatus,
  useCancelReminder,
  useSetReminder,
} from './use-auto-reminder'

function formatDateTime(iso: string | null): string {
  if (!iso) return '-'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

function Row({
  icon,
  label,
  children,
}: {
  icon?: React.ReactNode
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <span className="flex items-center gap-2 text-[12px] text-[var(--ink-3)]">
        {icon}
        {label}
      </span>
      <span className="text-right text-[13px] text-[var(--ink)]">{children}</span>
    </div>
  )
}

export function AutoReminderDetailsModal({
  resourceType,
  uuid,
  status,
  open,
  onOpenChange,
}: {
  resourceType: string
  uuid: string
  status: AutoReminderStatus
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const setReminder = useSetReminder(resourceType)
  const cancelReminder = useCancelReminder(resourceType)
  const [confirmCancel, setConfirmCancel] = useState(false)

  // Local cadence draft, seeded from status and re-seeded when it changes.
  const [interval, setInterval] = useState<string>('')
  const [maxCount, setMaxCount] = useState<string>('')
  useEffect(() => {
    setInterval(status.interval_days != null ? String(status.interval_days) : '')
    setMaxCount(status.max_reminders != null ? String(status.max_reminders) : '')
  }, [status.interval_days, status.max_reminders, open])

  const cadenceDirty =
    (status.interval_days != null ? String(status.interval_days) : '') !==
      interval ||
    (status.max_reminders != null ? String(status.max_reminders) : '') !== maxCount

  const saveCadence = () => {
    setReminder.mutate(
      {
        uuid,
        interval_days: interval === '' ? null : Number(interval),
        max_reminders: maxCount === '' ? null : Number(maxCount),
      },
      {
        onSuccess: () => toast.success('Reminder schedule updated.'),
        onError: (err) => toastApiError(err, 'Failed to update schedule'),
      },
    )
  }

  const toggleAutoCancel = (next: boolean) => {
    setReminder.mutate(
      { uuid, auto_cancel: next },
      { onError: (err) => toastApiError(err, 'Failed to update auto-cancel') },
    )
  }

  const nextSendLabel = status.is_capped
    ? 'All reminders sent'
    : status.next_send_at
      ? `${formatDateTime(status.next_send_at)}${status.is_due ? ' (due)' : ''}`
      : status.enabled
        ? 'Pending'
        : 'Paused'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BellRing className="h-4 w-4 text-[var(--status-ok)]" />
            {status.label}
          </DialogTitle>
          <DialogDescription>
            {status.enabled
              ? 'This record is being reminded automatically.'
              : 'Automatic reminders are off for this record.'}
          </DialogDescription>
        </DialogHeader>

        <div className="-my-1">
          <Row label="Status">
            {status.enabled ? (
              <Badge variant="default">Enabled</Badge>
            ) : (
              <Badge variant="outline">Off</Badge>
            )}
          </Row>
          <Separator />
          <Row icon={<Mail className="h-3.5 w-3.5" />} label="Recipient">
            {status.recipient_email || '-'}
          </Row>
          <Row icon={<Send className="h-3.5 w-3.5" />} label="Reminders sent">
            {status.sent_count}
            {status.max_reminders != null ? ` of ${status.max_reminders}` : ''}
          </Row>
          <Row icon={<CalendarClock className="h-3.5 w-3.5" />} label="Last sent">
            {formatDateTime(status.last_sent_at)}
          </Row>
          <Row icon={<CalendarClock className="h-3.5 w-3.5" />} label="Next send">
            {nextSendLabel}
          </Row>
          <Row label="Template">{status.template_name || '-'}</Row>
        </div>

        {status.supports_cadence_override && (
          <>
            <Separator />
            <div className="space-y-3">
              <p className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Schedule
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1.5 block text-[12px]">
                    Every (days)
                  </Label>
                  <Input
                    type="number"
                    min={1}
                    value={interval}
                    onChange={(e) => setInterval(e.target.value)}
                    placeholder="Tenant default"
                  />
                </div>
                <div>
                  <Label className="mb-1.5 block text-[12px]">
                    Max reminders
                  </Label>
                  <Input
                    type="number"
                    min={1}
                    value={maxCount}
                    onChange={(e) => setMaxCount(e.target.value)}
                    placeholder="Tenant default"
                  />
                </div>
              </div>
              {cadenceDirty && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={saveCadence}
                  disabled={setReminder.isPending}
                >
                  {setReminder.isPending ? 'Saving…' : 'Save schedule'}
                </Button>
              )}
            </div>
          </>
        )}

        {status.supports_auto_cancel && (
          <>
            <Separator />
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[13px] text-[var(--ink)]">Auto-cancel</p>
                <p className="text-[11px] text-[var(--ink-3)]">
                  Cancel this record when all reminders are exhausted.
                </p>
              </div>
              <Switch
                checked={status.auto_cancel_enabled}
                onCheckedChange={toggleAutoCancel}
                disabled={setReminder.isPending}
              />
            </div>
          </>
        )}

        <DialogFooter className="mt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {status.enabled && (
            <Button
              variant="destructive"
              onClick={() => setConfirmCancel(true)}
              disabled={cancelReminder.isPending}
            >
              Cancel auto-reminder
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Nested confirm - conditionally mounted (Base UI dialogs don't reliably
          close on an always-mounted `open` toggle). */}
      {confirmCancel && (
        <Dialog open onOpenChange={(o) => !o && setConfirmCancel(false)}>
          <DialogContent className="max-w-[400px]">
            <DialogHeader>
              <DialogTitle>Cancel this auto-reminder?</DialogTitle>
              <DialogDescription>
                No further {status.label.toLowerCase()} emails will be sent for
                this record. You can re-enable it later.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setConfirmCancel(false)}
                disabled={cancelReminder.isPending}
              >
                Keep it on
              </Button>
              <Button
                variant="destructive"
                onClick={() =>
                  cancelReminder.mutate(
                    { uuid },
                    {
                      onSuccess: () => {
                        toast.success('Auto-reminder cancelled.')
                        setConfirmCancel(false)
                        onOpenChange(false)
                      },
                      onError: (err) => {
                        toastApiError(err, 'Failed to cancel reminder')
                        setConfirmCancel(false)
                      },
                    },
                  )
                }
                disabled={cancelReminder.isPending}
              >
                {cancelReminder.isPending ? 'Cancelling…' : 'Cancel reminder'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  )
}
